// src/pages/Cart.jsx — v6
// CHANGED FROM v3: cart lines missing a required size/color (should be
// rare now given StoreContext v10's addToCart validation, but still
// possible from historical/pre-fix cart data) are now visibly flagged
// with a red "Fix" badge, and tapping the item passes an edit-mode
// state (editKey + current selection) via navigate — ProductDetail.jsx
// reads this to pre-fill what's already chosen and complete the SAME
// cart line in place instead of creating a duplicate. Everything else
// unchanged from v3.
// CHANGED FROM v4: FIXED needsSize check to also require category is
// Garments/Shoes (matching ProductDetail.jsx's real rule) — was
// wrongly flagging non-sized items (e.g. bags) as "size needed".
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import BottomNav from '../components/BottomNav';
import { useStore } from '../context/StoreContext';
import { pixelInitiateCheckout } from '../utils/pixel';
function CopyIdButton({ id }) {
  const [copied, setCopied] = useState(false);
  function handleCopy(e) {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <button onClick={handleCopy} className="ci-copy-btn" aria-label="Copy Product ID">
      <i className={`ti ${copied ? 'ti-check' : 'ti-copy'}`}></i>
    </button>
  );
}
export default function Cart() {
  const { cart, removeFromCart, updateQty, cartTotal } = useStore();
  const navigate = useNavigate();
  const hasShipExtra = cart.some(i => !i.shipping?.toUpperCase().includes('FREE'));
  function needsSizeFor(item) {
    return !!item.hasSizing && (item.category === 'Garments' || item.category === 'Shoes');
  }
  function itemMissingSelection(item) {
    const needsColor = (item.images?.length || 0) > 1;
    const missingColor = needsColor && !item.colorCode;
    const missingSize = needsSizeFor(item) && !item.size;
    return missingColor || missingSize;
  }
  function describeMissing(item) {
    const needsColor = (item.images?.length || 0) > 1;
    const missingColor = needsColor && !item.colorCode;
    const missingSize = needsSizeFor(item) && !item.size;
    if (missingColor && missingSize) return 'Color & size needed';
    if (missingColor) return 'Color needed';
    if (missingSize) return 'Size needed';
    return '';
  }
  function handleCheckout() {
    pixelInitiateCheckout(cartTotal, cart.reduce((s, i) => s + i.qty, 0));
    navigate('/checkout');
  }
  function goToProduct(item) {
    navigate(`/product/${item.id}`, {
      state: {
        editKey: item.key,
        editSize: item.size || '',
        editColorCode: item.colorCode || '',
        editQty: item.qty,
      },
    });
  }
  if (cart.length === 0) return (
    <div className="app-shell">
      <Navbar showBack title="Your cart" />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', textAlign: 'center' }}>
        <i className="ti ti-shopping-bag" style={{ fontSize: 52, color: 'var(--muted)', marginBottom: 16 }}></i>
        <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>Your cart is empty</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 24 }}>Add something you love</div>
        <button className="btn-primary" style={{ width: 180 }} onClick={() => navigate('/shop')}>Browse products</button>
      </div>
      <BottomNav />
    </div>
  );
  return (
    <div className="app-shell">
      <Navbar showBack title={`Cart (${cart.length})`} />
      <div className="page-body">
        {cart.map(item => {
          const itemFreeShip = item.shipping?.toUpperCase().includes('FREE');
          const incomplete = itemMissingSelection(item);
          return (
            <div key={item.key} className={`cart-item ${incomplete ? 'incomplete' : ''}`}>
              <div
                className={`ci-img ${item.category === 'Garments' ? 'ci-img-g' : 'ci-img-j'}`}
                onClick={() => goToProduct(item)}
                style={{ cursor: 'pointer' }}
              >
                {item.imageUrl
                  ? <img src={item.imageUrl} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <i className={`ti ${item.category === 'Garments' ? 'ti-shirt' : 'ti-diamond'}`} style={{ fontSize: 28, opacity: 0.3, color: 'var(--navy)' }}></i>
                }
              </div>
              <div className="ci-body">
                <div className="ci-name" onClick={() => goToProduct(item)} style={{ cursor: 'pointer' }}>{item.name}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span className="ci-id" onClick={() => goToProduct(item)} style={{ cursor: 'pointer', marginBottom: 0 }}>ID: {item.id}</span>
                  <CopyIdButton id={item.id} />
                </div>
                <div className="ci-meta">{item.size ? `Size: ${item.size} · ` : ''}{item.category}</div>
                {incomplete ? (
                  <button className="ci-fix-badge" onClick={() => goToProduct(item)}>
                    <i className="ti ti-alert-circle"></i> {describeMissing(item)} — tap to fix
                  </button>
                ) : (
                  <div style={{ fontSize: 10, color: itemFreeShip ? 'var(--green)' : '#D4A020', fontWeight: 500, marginBottom: 6 }}>
                    {itemFreeShip ? '✓ Free shipping' : '+ Shipping extra'}
                  </div>
                )}
                <div className="ci-footer">
                  <span className="ci-price">₹{(item.price * item.qty).toLocaleString()}</span>
                  <div className="ci-qty">
                    <button onClick={() => updateQty(item.key, item.qty - 1)}>−</button>
                    <span>{item.qty}</span>
                    <button onClick={() => updateQty(item.key, item.qty + 1)}>+</button>
                  </div>
                </div>
                <div className="ci-remove">
                  <button onClick={() => removeFromCart(item.key)}>
                    <i className="ti ti-trash"></i> Remove
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        <div className="card">
          <div className="section-title" style={{ marginBottom: 14 }}>Order summary</div>
          <div className="summary-row"><span>Subtotal ({cart.length} items)</span><span>₹{cartTotal.toLocaleString()}</span></div>
          <div className="summary-row" style={{ color: hasShipExtra ? '#D4A020' : 'var(--green)' }}>
            <span>Shipping</span>
            <span>{hasShipExtra ? 'To be confirmed' : 'Free'}</span>
          </div>
          <div className="summary-row total">
            <span>Total</span>
            <span>₹{cartTotal.toLocaleString()}{hasShipExtra ? ' + ship' : ''}</span>
          </div>
        </div>
        {hasShipExtra && (
          <div style={{ background: '#FEF8E8', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12, color: '#854F0B', lineHeight: 1.6 }}>
            <i className="ti ti-info-circle" style={{ marginRight: 6 }}></i>
            One or more items have shipping charges. Exact shipping amount will be confirmed via WhatsApp after order placement.
          </div>
        )}
        <button className="btn-primary" style={{ marginBottom: 10 }} onClick={handleCheckout}>
          Proceed to checkout →
        </button>
        <button className="btn-outline" onClick={() => navigate('/shop')}>Continue shopping</button>
        <div className="trust-row">
          {[['ti-lock', 'Secure payment'], ['ti-truck', 'Fast delivery'], ['ti-refresh', 'Easy returns']].map(([icon, text]) => (
            <div key={icon} className="trust-item">
              <i className={`ti ${icon}`} style={{ fontSize: 20, color: 'var(--blue)' }}></i>
              <span>{text}</span>
            </div>
          ))}
        </div>
      </div>
      <BottomNav />
      <style>{`
        .cart-item { background:#fff; border-radius:8px; padding:14px; display:flex; gap:12px; margin-bottom:10px; border:0.5px solid var(--card-border); }
        .cart-item.incomplete { border-color:#D64545; background:#FFFBFB; }
        .ci-img { width:72px; height:72px; border-radius:6px; display:flex; align-items:center; justify-content:center; flex-shrink:0; overflow:hidden; }
        .ci-img-g { background:#EEF2F8; }
        .ci-img-j { background:#F7F0E2; }
        .ci-body { flex:1; }
        .ci-name { font-size:13px; font-weight:500; color:var(--navy); margin-bottom:2px; }
        .ci-id { font-size:10px; color:var(--muted); letter-spacing:0.04em; margin-bottom:4px; }
        .ci-meta { font-size:11px; color:var(--muted); margin-bottom:4px; }
        .ci-copy-btn { background:var(--navy); border:none; color:#fff; width:20px; height:20px; border-radius:4px; display:flex; align-items:center; justify-content:center; cursor:pointer; padding:0; flex-shrink:0; }
        .ci-copy-btn i { font-size:11px; }
        .ci-fix-badge { display:flex; align-items:center; gap:5px; background:#D64545; color:#fff; border:none; padding:5px 9px; border-radius:5px; font-size:10px; font-weight:600; cursor:pointer; font-family:var(--font); margin-bottom:6px; }
        .ci-fix-badge i { font-size:12px; }
        .ci-footer { display:flex; justify-content:space-between; align-items:center; }
        .ci-price { font-size:15px; font-weight:500; }
        .ci-qty { display:flex; align-items:center; border:0.5px solid rgba(27,42,74,0.2); border-radius:5px; overflow:hidden; }
        .ci-qty button { width:28px; height:28px; border:none; background:transparent; font-size:16px; color:var(--navy); cursor:pointer; font-family:var(--font); }
        .ci-qty span { width:28px; text-align:center; font-size:13px; font-weight:500; }
        .ci-remove { display:flex; justify-content:flex-end; margin-top:8px; }
        .ci-remove button { border:none; background:none; font-size:11px; color:var(--red); cursor:pointer; font-family:var(--font); display:flex; align-items:center; gap:4px; }
        .summary-row { display:flex; justify-content:space-between; font-size:13px; color:#555; margin-bottom:10px; }
        .summary-row.total { border-top:0.5px solid var(--border); padding-top:12px; margin-top:4px; color:var(--navy); font-weight:500; font-size:15px; }
        .trust-row { display:flex; justify-content:center; gap:24px; padding:20px 0; }
        .trust-item { display:flex; flex-direction:column; align-items:center; gap:4px; }
        .trust-item span { font-size:10px; color:var(--muted); text-align:center; }
      `}</style>
    </div>
  );
}
